import {
  BrainEdgeDirection,
  BrainEdgeRelation,
  BrainEdgeStatus,
  BrainEntityType,
} from '@this+that/models';

import type {
  BrainEdgeRef,
  BrainEdgeView,
  BrainEntityView,
} from '../../../../../../../../models/companyBrain';

export enum BrainNeighbourGroup {
  WORKERS = 'workers',
  OWNERS = 'owners',
  PART_OF = 'partOf',
  CONTAINS = 'contains',
  WORKS_ON = 'worksOn',
  OWNS = 'owns',
  REPORTS_TO = 'reportsTo',
  DIRECT_REPORTS = 'directReports',
  CHANNELS = 'channels',
}

export enum BrainEdgeTone {
  ACTIVE = 'active',
  MUTED = 'muted',
  WARNING = 'warning',
}

export interface BrainNeighbourGroupView {
  group: BrainNeighbourGroup;
  edges: BrainEdgeView[];
}

/** Everything the neighbours list needs from the section that hosts it. */
export interface BrainGraphNavigation {
  teamId: string;
  canReveal: (entityId: string) => boolean;
  reveal: (entityId: string) => void;
  channelName: (channelId: string) => string | null;
  /** Admins only: confirm or dismiss a suggested edge. */
  canEditEdges: boolean;
}

const ENTITY_ROW_DOM_PREFIX = 'brain-entity-row-';
const PERSON_ROW_DOM_PREFIX = 'brain-person-row-';

const GROUP_BY_EDGE: Record<
  BrainEdgeRelation,
  Record<BrainEdgeDirection, BrainNeighbourGroup>
> = {
  [BrainEdgeRelation.WORKS_ON]: {
    [BrainEdgeDirection.IN]: BrainNeighbourGroup.WORKERS,
    [BrainEdgeDirection.OUT]: BrainNeighbourGroup.WORKS_ON,
  },
  [BrainEdgeRelation.OWNS]: {
    [BrainEdgeDirection.IN]: BrainNeighbourGroup.OWNERS,
    [BrainEdgeDirection.OUT]: BrainNeighbourGroup.OWNS,
  },
  [BrainEdgeRelation.PART_OF]: {
    [BrainEdgeDirection.IN]: BrainNeighbourGroup.CONTAINS,
    [BrainEdgeDirection.OUT]: BrainNeighbourGroup.PART_OF,
  },
  [BrainEdgeRelation.REPORTS_TO]: {
    [BrainEdgeDirection.IN]: BrainNeighbourGroup.DIRECT_REPORTS,
    [BrainEdgeDirection.OUT]: BrainNeighbourGroup.REPORTS_TO,
  },
  [BrainEdgeRelation.LIVES_IN]: {
    [BrainEdgeDirection.IN]: BrainNeighbourGroup.CHANNELS,
    [BrainEdgeDirection.OUT]: BrainNeighbourGroup.CHANNELS,
  },
};

export const BRAIN_NEIGHBOUR_GROUP_ORDER: readonly BrainNeighbourGroup[] = [
  BrainNeighbourGroup.WORKERS,
  BrainNeighbourGroup.OWNERS,
  BrainNeighbourGroup.PART_OF,
  BrainNeighbourGroup.CONTAINS,
  BrainNeighbourGroup.WORKS_ON,
  BrainNeighbourGroup.OWNS,
  BrainNeighbourGroup.REPORTS_TO,
  BrainNeighbourGroup.DIRECT_REPORTS,
  BrainNeighbourGroup.CHANNELS,
];

const STATUS_RANK: Record<BrainEdgeStatus, number> = {
  [BrainEdgeStatus.ACTIVE]: 0,
  [BrainEdgeStatus.DISPUTED]: 1,
  [BrainEdgeStatus.NEEDS_CONFIRMATION]: 1,
  [BrainEdgeStatus.SUPERSEDED]: 2,
};

const TONE_BY_STATUS: Record<BrainEdgeStatus, BrainEdgeTone> = {
  [BrainEdgeStatus.ACTIVE]: BrainEdgeTone.ACTIVE,
  [BrainEdgeStatus.DISPUTED]: BrainEdgeTone.WARNING,
  [BrainEdgeStatus.NEEDS_CONFIRMATION]: BrainEdgeTone.WARNING,
  [BrainEdgeStatus.SUPERSEDED]: BrainEdgeTone.MUTED,
};

export function brainEdgeTone(status: BrainEdgeStatus): BrainEdgeTone {
  return TONE_BY_STATUS[status];
}

function isRenderableEdge(edge: BrainEdgeView): boolean {
  if (edge.relation === BrainEdgeRelation.LIVES_IN) {
    return !!edge.channelId;
  }
  return !!edge.neighbour;
}

function compareEdges(left: BrainEdgeView, right: BrainEdgeView): number {
  const byStatus = STATUS_RANK[left.status] - STATUS_RANK[right.status];
  return byStatus !== 0
    ? byStatus
    : right.lastSeen.localeCompare(left.lastSeen);
}

export function groupBrainEdges(
  edges: readonly BrainEdgeView[],
): BrainNeighbourGroupView[] {
  const byGroup = new Map<BrainNeighbourGroup, BrainEdgeView[]>();
  for (const edge of edges.filter(isRenderableEdge)) {
    const group = GROUP_BY_EDGE[edge.relation][edge.direction];
    byGroup.set(group, [...(byGroup.get(group) ?? []), edge]);
  }
  return BRAIN_NEIGHBOUR_GROUP_ORDER.flatMap((group) => {
    const grouped = byGroup.get(group);
    return grouped ? [{ group, edges: [...grouped].sort(compareEdges) }] : [];
  });
}

const EDITABLE_RELATIONS: readonly BrainEdgeRelation[] = [
  BrainEdgeRelation.OWNS,
  BrainEdgeRelation.WORKS_ON,
  BrainEdgeRelation.REPORTS_TO,
];

/** The graph lists edges relative to the expanded node; the mutations want them oriented from → to. */
export function toBrainEdgeRef(
  entityId: string,
  edge: BrainEdgeView,
): BrainEdgeRef | null {
  const neighbourId = edge.neighbour?.entityId;
  if (!neighbourId || !EDITABLE_RELATIONS.includes(edge.relation)) {
    return null;
  }
  const isOutgoing = edge.direction === BrainEdgeDirection.OUT;
  return {
    fromEntityId: isOutgoing ? entityId : neighbourId,
    relation: edge.relation,
    toEntityId: isOutgoing ? neighbourId : entityId,
  };
}

export function isPendingSuggestion(edge: BrainEdgeView): boolean {
  return edge.suggested && edge.status === BrainEdgeStatus.NEEDS_CONFIRMATION;
}

export function brainEdgeKey(edge: BrainEdgeView): string {
  const target = edge.neighbour?.entityId ?? edge.channelId;
  return `${edge.relation}:${edge.direction}:${target}`;
}

/** Edges load on expand, so the toggle relies on what implies an edge: children, a parent, facts, or being a person. */
export function entityMayHaveGraph(
  entity: Pick<
    BrainEntityView,
    'children' | 'parentEntityId' | 'activeAssertionCount' | 'type'
  >,
): boolean {
  return (
    (entity.children?.length ?? 0) > 0 ||
    !!entity.parentEntityId ||
    entity.activeAssertionCount > 0 ||
    entity.type === BrainEntityType.PERSON
  );
}

export function brainEntityRowDomId(entityId: string): string {
  return `${ENTITY_ROW_DOM_PREFIX}${entityId}`;
}

export function brainPersonRowDomId(entityId: string): string {
  return `${PERSON_ROW_DOM_PREFIX}${entityId}`;
}
